import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Card, ExplainerBanner, Icon, Skeleton, StatusChip } from '@/ds'
import { dayMonth, formatMoney, formatMoneyShort } from '@/lib/format'
import { useAppNavigate, withQuery } from '@/lib/query-params'
import type { Deposit } from '@/services/fianza'
import { depositDescription } from '../../Fianza/copy'
import { changeQuery } from '../../Reserva/shared'
import type { ChangeOrigin } from '../../Reserva/shared'
import { STEP_ROUTES } from '../steps'
import type { Async } from '@/lib/useAsync'
import type { StayData } from '../useStayData'

/** Explicador de la fianza el día de salida (bundle B1 fase 4). Neutro sin tipo; texto del tipo si llega (inventario D7). */
export function DepositExplainer({ deposit, lang, origin }: { deposit: Async<Deposit | null>; lang: string; origin: ChangeOrigin }) {
  const { t } = useTranslation()
  const { go } = useAppNavigate()
  if (deposit.kind === 'loading') return <Skeleton height={120} radius={16} />
  if (deposit.kind !== 'ready' || !deposit.value) return null
  const d = deposit.value
  if (d.status !== 'held' && d.status !== 'review') return null
  return (
    <ExplainerBanner title={t('v2.estancia.fianzaExplicador.titulo')} moreLabel={t('v2.estancia.fianzaExplicador.seguir')} onMore={() => go(STEP_ROUTES.deposit, { query: changeQuery(origin) })}>
      {depositDescription(d, lang, t)}
    </ExplainerBanner>
  )
}


function chipFor(d: Deposit, lang: string, t: TFunction): { tone: 'success' | 'warning' | 'info' | 'neutral'; text: string } {
  switch (d.status) {
    case 'released':
    case 'refunded':
      return { tone: 'success', text: t('v2.estancia.despues.liberadaEl', { date: d.releasedAt ? dayMonth(d.releasedAt, lang) : '' }).trim() }
    case 'review':
      return { tone: 'warning', text: d.reviewDeadline ? t('v2.estancia.despues.enRevision', { date: dayMonth(d.reviewDeadline, lang) }) : t('v2.estancia.despues.enRevisionSinFecha') }
    case 'held':
    case 'deducted':
      return { tone: 'info', text: t('v2.estancia.despues.retenida') }
    default:
      return { tone: 'neutral', text: t('v2.estancia.despues.pendiente') }
  }
}

/** "¿Me han devuelto ya la fianza?" (bundle B1 fase 5): estado, fecha y margen del banco. */
export function DepositAfterCard({ deposit, lang, origin }: { deposit: Async<Deposit | null>; lang: string; origin: ChangeOrigin }) {
  const { t } = useTranslation()
  const { go, search } = useAppNavigate()
  if (deposit.kind === 'loading') return <Skeleton height={140} radius={16} />
  if (deposit.kind !== 'ready' || !deposit.value) return null
  const d = deposit.value
  const chip = chipFor(d, lang, t)
  const text = depositDescription(d, lang, t)
  return (
    <Card as="section" className="estancia__deposit">
      <div className="estancia__deposit-head">
        <h2 className="estancia__card-title">{t('v2.estancia.despues.fianzaDe', { amount: formatMoneyShort(d.amount, lang) })}</h2>
        <StatusChip tone={chip.tone}>{chip.text}</StatusChip>
      </div>
      <p className="estancia__card-text">{text}</p>
      <a
        href={withQuery(STEP_ROUTES.deposit, search, changeQuery(origin))}
        onClick={(e) => {
          e.preventDefault()
          go(STEP_ROUTES.deposit, { query: changeQuery(origin) })
        }}
        className="estancia__inline-link"
      >
        {t('v2.estancia.despues.verSeguimiento')}
        <Icon name="chevron-right" size={20} />
      </a>
    </Card>
  )
}

/** Subtítulo de "Cargos de la estancia": la API actual solo da el total (D25). */
export function chargesSubtitle(data: StayData, lang: string, t: TFunction): string | null {
  if (data.payment.kind !== 'ready') return null
  return t('v2.estancia.cargos.total', { amount: formatMoney(data.payment.value.total, lang) })
}
